import { Fragment, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ValidationError, ParsedCSVData } from '@/types/upload';
import { decodeCsvBytes, parseCSV } from '@/utils/csvParser';
import {
  csvRowsToObjects,
  getEntityType,
  splitFieldMessage,
  validateByUploadType,
  validateCSVHeaders,
} from '@/utils/validation';
import { getPresignedUrl, uploadToS3, deleteUploadRecord } from '@/utils/uploadApi';

interface CSVUploadProps {
  uploadType: string;
  onUploadSuccess?: (data: ParsedCSVData) => void;
  /**
   * クライアント側の全件バリデーション関数（省略時は uploadType に応じた既定の Zod 検証）。
   * 1行=1エンティティに収まらない CSV（例: 階層構造の Menu）は専用の関数を渡す。
   * いずれの場合も presigned URL 取得前に全ルールを検証し、エラーが無い場合のみプレビュー（登録）を表示する。
   * CSV は Book / Product と同じ 3 行構成（1=定義名 / 2=日本語表示名 / 3 行目以降=データ）を前提とし、
   * プレビュー見出しには 2 行目（表示名）を使用する。
   */
  customValidate?: (data: ParsedCSVData) => ValidationError[];
}

const CSVUpload = ({
  uploadType,
  onUploadSuccess,
  customValidate,
}: CSVUploadProps) => {
  const { user } = useAuth();
  const uploadUser = user?.name || user?.email || 'unknown';
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [parsedData, setParsedData] = useState<ParsedCSVData | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const validateFile = (file: File): boolean => {
    if (!file.name.endsWith('.csv')) {
      setError('CSVファイルを選択してください');
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('ファイルサイズは10MB以下にしてください');
      return false;
    }
    return true;
  };

  const validateCSVData = (data: ParsedCSVData): ValidationError[] => {
    // 専用バリデーション関数が渡された場合はそれを使う（presign 前に全ルール検証）
    if (customValidate) {
      return customValidate(data);
    }

    const errors: ValidationError[] = [];

    // ヘッダーバリデーション（スキーマ照合 + 列数チェック）
    const headerErrors = validateCSVHeaders(data.headers, data.rows, uploadType);
    if (headerErrors.length > 0) {
      headerErrors.forEach(msg => {
        errors.push({ row: 1, column: 'ヘッダー', value: '', error: msg });
      });
      return errors; // ヘッダーが不正な場合はフィールドバリデーションをスキップ
    }

    // CSV配列をオブジェクトに変換（アップロードタイプに応じたエンティティ設定を使用）
    const entityType = getEntityType(uploadType);
    const objects = csvRowsToObjects(data.headers, data.rows, entityType);

    // アップロードタイプに応じたZodバリデーション
    const { invalid } = validateByUploadType(uploadType, objects);

    // エラーを ValidationError 形式に変換
    invalid.forEach(({ row, errors: zodErrors }) => {
      zodErrors.forEach(errorMsg => {
        // `field: message` の分解は validation 側のヘルパーに寄せる（キャストを使わない）。
        const { field, message } = splitFieldMessage(errorMsg);
        errors.push({
          row: row + 1, // ヘッダー行を考慮
          column: field,
          // `objects` の値は CSV 由来の unknown。String() は unknown を受け取れるため
          // キャストせずそのまま渡す。
          value: String(objects[row - 1]?.[field] ?? ''),
          error: message
        });
      });
    });

    return errors;
  };

  const processFile = async (file: File) => {
    setIsValidating(true);
    setError('');
    setValidationErrors([]);

    try {
      const text = decodeCsvBytes(await file.arrayBuffer());
      const data = parseCSV(text);
      setParsedData(data);

      const errors = validateCSVData(data);

      if (errors.length > 0) {
        setValidationErrors(errors);
      }
    } catch (err) {
      setError('ファイルの読み込みに失敗しました');
    } finally {
      setIsValidating(false);
    }
  };

  /**
   * 選択が無効になったときの状態リセット。
   *
   * `selectedFile` だけを null にすると、**前のファイルの `parsedData` が残ったまま**
   * 「登録」ボタン（描画条件は `parsedData`）が表示され続け、押すと `handleUpload` の
   * ガード（`!selectedFile`）に落ちて無反応になる。プレビューと選択状態は常に一致させる。
   */
  const clearSelection = () => {
    setSelectedFile(null);
    setParsedData(null);
    setValidationErrors([]);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (validateFile(file)) {
      setSelectedFile(file);
      await processFile(file);
    } else {
      clearSelection();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (validateFile(file)) {
      setSelectedFile(file);
      await processFile(file);
    } else {
      clearSelection();
    }
  };

  const handleUpload = async () => {
    // 「登録」ボタンは parsedData がある時だけ描画される。`selectedFile` と `parsedData` は
    // 「両方セット（validateFile 通過 → processFile）」か「両方クリア（clearSelection）」の
    // どちらかにしかならないよう揃えているため、このガードは実行経路上到達しない。
    // ※以前は選択失敗時に selectedFile だけを null にしていたため、**旧 parsedData が残って
    //   ここへ到達し得た**（登録ボタンを押しても何も起きない）。clearSelection で解消済み。
    /* v8 ignore next -- selectedFile と parsedData を常に同時更新しているため到達不能 */
    if (!selectedFile || !parsedData) return;

    setIsUploading(true);
    setError('');
    let currentUploadId: string | null = null;

    try {
      // Interceptorが自動的にJWTトークンを追加
      const { uploadUrl, uploadId } = await getPresignedUrl(
        selectedFile.name,
        uploadType,
        uploadUser
      );

      currentUploadId = uploadId;

      // S3にアップロード
      await uploadToS3(uploadUrl, selectedFile);

      onUploadSuccess?.(parsedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'アップロードに失敗しました');
      
      // S3アップロード失敗時のみDBレコード削除
      if (currentUploadId) {
        try {
          await deleteUploadRecord(currentUploadId);
        } catch {
          // Cleanup failure is non-critical
        }
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
        }`}
      >
        <div className="mb-4">
          <span className="text-4xl">📁</span>
        </div>
        <p className="text-admin-text mb-2">ここにCSVファイルをドラッグ&ドロップ</p>
        <p className="text-sm text-admin-textLight mb-4">または</p>
        <label className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
          ファイルを選択
          <input type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
        </label>
      </div>

      {isValidating && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-600">🔄 検証中...</p>
        </div>
      )}

      {isUploading && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-600">🔄 アップロード中...</p>
        </div>
      )}

      {selectedFile && !isValidating && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-admin-text">
            <span className="font-semibold">選択したファイル：</span>
            <span className="ml-2">{selectedFile.name}</span>
            <span className="ml-2 text-admin-textLight">
              ({(selectedFile.size / 1024).toFixed(2)} KB)
            </span>
          </p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">⚠️ {error}</p>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="font-semibold text-red-600 mb-2">
            ❌ バリデーションエラー ({validationErrors.length}件)
          </h3>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-red-100">
                <tr>
                  <th className="p-2 text-left">行</th>
                  <th className="p-2 text-left">列</th>
                  <th className="p-2 text-left">値</th>
                  <th className="p-2 text-left">エラー</th>
                </tr>
              </thead>
              <tbody>
                {validationErrors.map((err, idx) => (
                  <tr key={idx} className="border-t border-red-200">
                    <td className="p-2">{err.row}</td>
                    <td className="p-2">{err.column}</td>
                    <td className="p-2 truncate max-w-xs">{err.value || '(空)'}</td>
                    <td className="p-2 text-red-600">{err.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {parsedData && validationErrors.length === 0 && !isValidating && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-600 mb-4">✅ プレビュー</h3>
          <div className="overflow-x-auto max-h-96">
            {(() => {
              const introIdx = parsedData.headers.indexOf('introduction');
              const mainHeaders = parsedData.displayHeaders.filter((_, idx) => idx !== introIdx);
              const colCount = mainHeaders.length;

              return (
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-green-100 sticky top-0">
                    <tr>
                      {mainHeaders.map((header, idx) => (
                        <th key={idx} className="p-2 text-left border border-green-200 whitespace-nowrap">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.rows.map((row, rowIdx) => {
                      const mainCells = row.filter((_, idx) => idx !== introIdx);
                      const introText = introIdx >= 0 ? row[introIdx] : '';
                      return (
                        <Fragment key={rowIdx}>
                          <tr className={rowIdx > 0 ? "border-t-2 border-t-green-400" : ""}>
                            {mainCells.map((cell, cellIdx) => (
                              <td key={cellIdx} className="p-2 border border-green-200 whitespace-nowrap">
                                {cell}
                              </td>
                            ))}
                          </tr>
                          {introIdx >= 0 && introText && (
                            <tr className="bg-green-50/50">
                              <td colSpan={colCount} className="p-2 border border-green-200 whitespace-pre-wrap text-admin-textLight">
                                {introText}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-green-600">
              合計 {parsedData.rows.length} 件のデータが正常に検証されました
            </p>
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isUploading ? 'アップロード中...' : '登録'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSVUpload;
